// Meta catalog feed — a brand-scoped URL Meta Commerce Manager fetches
// on a schedule. Contract: liquidretail_backend #642.
//
//   GET  /api/brand/:id/meta-catalog-feed
//        { configured, createdAt, setAt, slug, brand, urlMasked }
//        404 { error: 'brand not found' }
//   POST /api/brand/:id/meta-catalog-feed/token
//        body { confirmReplace?: true }
//        { ok, replaced, brand, slug, setAt, token, url }
//        409 { ok: false, code: 'confirm-replace' | 'no-slug' }
//   GET  /api/brand/:id/meta-catalog-feed/preview
//        counts only; 503 { error: 'preview-unavailable', code }
//
// The token POST response is the only one that carries the full feed URL.
// `token` is a separate secret and is never rendered, copied, or stored.
// The revealed URL lives in component state for this view only — not in
// localStorage, sessionStorage, logs, or analytics. All three calls are
// sent cache: 'no-store' to match the server's no-store responses.
//
// Description copy (liquidretail_backend #663, branch
// cursor/plan-copy-stock-build-8b68). The feed key is never part of this
// UI. Nothing is spent until the operator confirms a USD cap.
//
//   POST /api/brand/:id/meta-catalog-feed/copy/estimate
//        body {}
//        { products, proof, skip, estimatedCostUsd }
//        `products` is a count, or a list whose length is the count and
//        whose title/name strings are shown. Cost may also arrive as
//        estimatedUsd.
//   POST /api/brand/:id/meta-catalog-feed/copy/generate
//        { capUsd, onlyRejected?: true }
//        onlyRejected is the "Retry too-long" path.
//   GET  /api/brand/:id/meta-catalog-feed/copy/status
//        { status: 'queued'|'running'|'done'|'failed'|'cancelled',
//          ready, proof, rejected, tooLong?,
//          reason? | failureReason? | error?,
//          spentUsd? | spendUsd? | actualCostUsd? | costSoFarUsd? }
//        Polled until done, failed, or cancelled, and stopped after a
//        client-side timeout if the job never reaches one of those.
//   POST /api/brand/:id/meta-catalog-feed/copy/cancel
//        body {}
//        Cancels the brand's open copy job (liquidretail_backend #670).
//        A worker-off refusal is an error whose code or error is
//        worker-off (or another worker-off slug) and whose reason, when
//        present, is the sentence to show. A failed or cancelled status
//        carries that sentence on reason. Spend so far is spentUsd.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter,
  AlertDialogHeader, AlertDialogOverlay, Badge, Box, Button, Card, CardBody,
  Heading, HStack, Icon, Input, SimpleGrid, Spinner, Text, VStack, useDisclosure, useToast
} from '@chakra-ui/react';
import { apiJson, isApiError } from '../../auth/apiFetch';

type FeedStatus = {
  configured: boolean;
  createdAt: string | null;
  setAt: string | null;
  slug?: string | null;
  brand?: string | null;
  urlMasked: string | null;
};

type TokenCreated = {
  ok?: boolean;
  replaced?: boolean;
  brand?: string | null;
  slug?: string | null;
  setAt?: string | null;
  token?: string;
  url?: string;
};

type FeedPreview = {
  rows?: number;
  skippedGiftCards?: number;
  skippedOutOfStock?: number;
  skippedNoImage?: number;
  withLifestyleImage?: number;
  withCatalogFallbackImage?: number;
  withShopifyFallbackImage?: number;
  withAdditionalImage?: number;
  withVideo?: number;
  currency?: string | null;
  cache?: unknown;
};

type Props = { brandId: string };

type CopyEstimateView = {
  products: number | null;
  titles: string[];
  proof: number | null;
  skip: number | null;
  estimatedCostUsd: number | null;
};

type CopyRunView = {
  phase: 'running' | 'done' | 'failed' | 'cancelled';
  ready: number | null;
  proof: number | null;
  rejected: number | null;
  tooLong: number | null;
  spentUsd: number | null;
  error: string | null;
};

type CopyPhase = 'idle' | 'estimating' | 'estimated' | 'starting' | 'polling' | 'done' | 'failed' | 'cancelled' | 'timedOut';

const COPY_POLL_MS = 2000;
const COPY_POLL_GIVE_UP = 5;
/** Stop checking status if the job never reaches done, failed, or cancelled. */
export const COPY_POLL_TIMEOUT_MS = 10 * 60 * 1000;

const PREVIEW_COUNTS: Array<{ key: keyof FeedPreview; label: string }> = [
  { key: 'rows', label: 'Rows' },
  { key: 'withLifestyleImage', label: 'Lifestyle images' },
  { key: 'withAdditionalImage', label: 'Extra images' },
  { key: 'withVideo', label: 'Videos' },
  { key: 'withCatalogFallbackImage', label: 'Catalog fallback images' },
  { key: 'withShopifyFallbackImage', label: 'Shopify fallback images' },
  { key: 'skippedGiftCards', label: 'Skipped gift cards' },
  { key: 'skippedOutOfStock', label: 'Skipped out of stock' },
  { key: 'skippedNoImage', label: 'Skipped, no image' }
];

export function MetaCatalogFeedCard({ brandId }: Props) {
  const toast = useToast();
  const confirm = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [status, setStatus] = useState<FeedStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  // In-memory only. Cleared when the brand changes. Never written anywhere else.
  const [revealedUrl, setRevealedUrl] = useState<string | null>(null);
  const [preview, setPreview] = useState<FeedPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [copyPhase, setCopyPhase] = useState<CopyPhase>('idle');
  const [copyEstimate, setCopyEstimate] = useState<CopyEstimateView | null>(null);
  const [copyRun, setCopyRun] = useState<CopyRunView | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [capInput, setCapInput] = useState('');
  const [capTouched, setCapTouched] = useState(false);
  const capTouchedRef = useRef(false);
  capTouchedRef.current = capTouched;
  const requestSeq = useRef(0);
  const brandRef = useRef(brandId);
  const pollBrand = useRef<string | null>(null);
  const ignorePoll = useRef(false);
  const [pollNonce, setPollNonce] = useState(0);
  brandRef.current = brandId;

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const seq = ++requestSeq.current;
    if (!opts?.silent) setLoading(true);
    if (!opts?.silent) setLoadError(null);
    try {
      const data = await apiJson<FeedStatus>(`/api/brand/${brandId}/meta-catalog-feed`, { cache: 'no-store' });
      if (seq !== requestSeq.current) return;
      setStatus(data);
      setLoadError(null);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      if (opts?.silent) return;
      setStatus(null);
      setLoadError(loadErrorMessage(err));
    } finally {
      if (seq === requestSeq.current && !opts?.silent) setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    setRevealedUrl(null);
    setPreview(null);
    setPreviewError(null);
    setActionError(null);
    pollBrand.current = null;
    ignorePoll.current = false;
    setCopyPhase('idle');
    setCopyEstimate(null);
    setCopyRun(null);
    setCopyError(null);
    setCancelling(false);
    setCapInput('');
    setCapTouched(false);
  }, [brandId]);

  useEffect(() => { void load(); }, [load]);

  const createToken = async (confirmReplace: boolean) => {
    const forBrand = brandId;
    setBusy(true);
    setActionError(null);
    try {
      const data = await apiJson<TokenCreated>(`/api/brand/${forBrand}/meta-catalog-feed/token`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(confirmReplace ? { confirmReplace: true } : {})
      });
      if (brandRef.current !== forBrand) return;
      // Only `url` is shown. `token` stays on the response object and is never copied out.
      const url = typeof data.url === 'string' && data.url ? data.url : null;
      if (url) setRevealedUrl(url);
      confirm.onClose();
      await load({ silent: true });
    } catch (err: unknown) {
      if (brandRef.current !== forBrand) return;
      if (isApiError(err) && err.status === 409) {
        const code = errorCode(err.body);
        if (code === 'confirm-replace') {
          confirm.onOpen();
          return;
        }
        if (code === 'no-slug') {
          confirm.onClose();
          setActionError('This brand has no slug, so a feed URL can’t be generated.');
          return;
        }
      }
      if (isApiError(err) && err.status === 404) {
        setActionError('Brand not found.');
        return;
      }
      const message = err instanceof Error ? err.message : 'Could not generate a feed URL.';
      setActionError(message);
      toast({ title: 'Feed URL failed', description: message, status: 'error', duration: 4000 });
    } finally {
      setBusy(false);
    }
  };

  const loadPreview = async () => {
    setPreviewing(true);
    setPreviewError(null);
    try {
      const data = await apiJson<FeedPreview>(`/api/brand/${brandId}/meta-catalog-feed/preview`, { cache: 'no-store' });
      setPreview(data);
    } catch (err: unknown) {
      setPreview(null);
      if (isApiError(err) && err.status === 503) {
        const code = errorCode(err.body);
        setPreviewError(code ? `Preview is unavailable (${code}).` : 'Preview is unavailable.');
        return;
      }
      if (isApiError(err) && err.status === 404) {
        setPreviewError('Brand not found.');
        return;
      }
      const message = err instanceof Error ? err.message : 'Could not load the preview.';
      setPreviewError(message);
      toast({ title: 'Preview failed', description: message, status: 'error', duration: 4000 });
    } finally {
      setPreviewing(false);
    }
  };

  const estimateCopy = async () => {
    const forBrand = brandId;
    setCopyPhase('estimating');
    setCopyError(null);
    setCopyRun(null);
    try {
      const data = await apiJson<unknown>(`/api/brand/${forBrand}/meta-catalog-feed/copy/estimate`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });
      if (brandRef.current !== forBrand) return;
      const view = readCopyEstimate(data);
      setCopyEstimate(view);
      if (!capTouchedRef.current && view.estimatedCostUsd != null) {
        setCapInput(formatCap(roundUsdUp(view.estimatedCostUsd)));
      }
      setCopyPhase('estimated');
    } catch (err: unknown) {
      if (brandRef.current !== forBrand) return;
      setCopyPhase('failed');
      setCopyError(copyErrorMessage(err, 'Could not estimate description cost.'));
    }
  };

  const startCopy = async (onlyRejected: boolean) => {
    const capUsd = parseCap(capInput);
    if (capUsd == null) {
      setCopyError('Enter a USD cap before generating.');
      return;
    }
    const forBrand = brandId;
    setCopyPhase('starting');
    setCopyError(null);
    try {
      const data = await apiJson<unknown>(`/api/brand/${forBrand}/meta-catalog-feed/copy/generate`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(onlyRejected ? { capUsd, onlyRejected: true } : { capUsd })
      });
      if (brandRef.current !== forBrand) return;
      const view = readCopyRun(data);
      if (view.phase !== 'running') {
        setCopyRun(view);
        setCopyPhase(view.phase);
        setCopyError(view.phase === 'done' ? null : (view.error || 'Description generation failed.'));
        return;
      }
      ignorePoll.current = false;
      pollBrand.current = forBrand;
      setCopyPhase('polling');
    } catch (err: unknown) {
      if (brandRef.current !== forBrand) return;
      setCopyPhase('failed');
      setCopyError(copyErrorMessage(err, 'Could not start description generation.'));
    }
  };

  const cancelCopy = async () => {
    const forBrand = brandId;
    ignorePoll.current = true;
    setCancelling(true);
    setCopyError(null);
    try {
      const data = await apiJson<unknown>(`/api/brand/${forBrand}/meta-catalog-feed/copy/cancel`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: '{}'
      });
      if (brandRef.current !== forBrand) return;
      pollBrand.current = null;
      const view = readCopyRun(data);
      const phase = view.phase === 'running' ? 'cancelled' : view.phase;
      setCopyRun(phase === view.phase ? view : { ...view, phase });
      setCopyPhase(phase === 'done' ? 'done' : phase === 'failed' ? 'failed' : 'cancelled');
      setCopyError(phase === 'failed' ? (view.error || 'Description generation failed.') : view.error);
    } catch (err: unknown) {
      if (brandRef.current !== forBrand) return;
      ignorePoll.current = false;
      setCopyError(copyErrorMessage(err, 'Could not cancel description generation.'));
      setPollNonce(value => value + 1);
    } finally {
      if (brandRef.current === forBrand) setCancelling(false);
    }
  };

  useEffect(() => {
    if (copyPhase !== 'polling' || pollBrand.current !== brandId) return;
    const forBrand = brandId;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let failures = 0;
    const startedAt = Date.now();

    const arm = () => {
      timer = setTimeout(() => { void tick(); }, COPY_POLL_MS);
    };

    const tick = async () => {
      try {
        const data = await apiJson<unknown>(`/api/brand/${forBrand}/meta-catalog-feed/copy/status`, { cache: 'no-store' });
        if (cancelled || ignorePoll.current || brandRef.current !== forBrand) return;
        failures = 0;
        const view = readCopyRun(data);
        setCopyRun(view);
        if (view.phase === 'done') {
          setCopyPhase('done');
          setCopyError(null);
          return;
        }
        if (view.phase === 'cancelled') {
          setCopyPhase('cancelled');
          setCopyError(view.error);
          return;
        }
        if (view.phase === 'failed') {
          setCopyPhase('failed');
          setCopyError(view.error || 'Description generation failed.');
          return;
        }
        if (Date.now() - startedAt >= COPY_POLL_TIMEOUT_MS) {
          setCopyPhase('timedOut');
          setCopyError('Stopped checking because this description job did not finish. You can cancel it if it is still open.');
          return;
        }
      } catch (err: unknown) {
        if (cancelled || ignorePoll.current || brandRef.current !== forBrand) return;
        failures += 1;
        const terminal = isApiError(err) && err.status >= 400 && err.status < 500;
        if (terminal || failures >= COPY_POLL_GIVE_UP) {
          setCopyPhase('failed');
          setCopyError(copyErrorMessage(err, 'Could not read description status.'));
          return;
        }
      }
      if (!cancelled && !ignorePoll.current) arm();
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [copyPhase, brandId, pollNonce]);

  const copyUrl = async () => {
    if (!revealedUrl) return;
    try {
      await navigator.clipboard.writeText(revealedUrl);
      toast({ title: 'Feed URL copied', status: 'success', duration: 2000 });
    } catch {
      toast({ title: 'Copy failed', description: 'Select the URL and copy it manually.', status: 'error', duration: 4000 });
    }
  };

  const configured = status?.configured === true;

  return (
    <Card variant="outline" id="meta-catalog-feed">
      <CardBody>
        <HStack mb={3} spacing={2} justify="space-between">
          <HStack spacing={2}>
            <FeedIcon />
            <Heading size="sm" textTransform="uppercase" letterSpacing="0.04em">Meta catalog feed</Heading>
          </HStack>
          {status && (
            <Badge colorScheme={configured ? 'green' : 'gray'} variant="subtle">
              {configured ? 'Active' : 'Not configured'}
            </Badge>
          )}
        </HStack>

        {loading && !status ? (
          <HStack spacing={2}>
            <Spinner size="sm" />
            <Text fontSize="sm" color="brand.muted">Loading feed…</Text>
          </HStack>
        ) : loadError && !status ? (
          <Text fontSize="sm" color="red.600">{loadError}</Text>
        ) : status ? (
          <VStack align="stretch" spacing={3}>
            <VStack align="stretch" spacing={1.5}>
              <Row label="Created" value={formatDate(status.createdAt)} />
              <Row label="Last rotated" value={formatDate(status.setAt)} />
              <Row
                label="Feed URL"
                value={status.urlMasked
                  ? <Text as="span" fontFamily="mono" fontSize="xs" wordBreak="break-all">{status.urlMasked}</Text>
                  : '—'}
              />
            </VStack>

            {revealedUrl && (
              <Box
                borderWidth="1px"
                borderColor="rsViolet.200"
                bg="rsViolet.50"
                borderRadius="md"
                px={3}
                py={2.5}
              >
                <Text fontSize="xs" color="brand.muted" mb={1}>Feed URL</Text>
                <Text fontFamily="mono" fontSize="xs" color="brand.ink" wordBreak="break-all">{revealedUrl}</Text>
                <HStack mt={2} spacing={3} align="center">
                  <Button size="xs" variant="outline" onClick={() => { void copyUrl(); }}>Copy</Button>
                  <Text fontSize="xs" color="brand.muted">This URL won't be shown again.</Text>
                </HStack>
              </Box>
            )}

            <Text fontSize="xs" color="brand.muted">
              Paste it in Meta Commerce Manager under Data sources, then Data feed, using a scheduled fetch.
            </Text>

            {actionError && <Text fontSize="sm" color="red.600">{actionError}</Text>}

            <HStack spacing={2} flexWrap="wrap">
                {configured ? (
                  <Button size="sm" variant="outline" onClick={confirm.onOpen} isDisabled={busy}>
                    Rotate
                  </Button>
                ) : (
                  <Button size="sm" variant="brand" onClick={() => { void createToken(false); }} isLoading={busy} loadingText="Generating…">
                    Generate feed URL
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { void estimateCopy(); }}
                  isLoading={copyPhase === 'estimating'}
                  loadingText="Estimating…"
                  isDisabled={busy || copyPhase === 'starting' || copyPhase === 'polling'}
                >
                  Generate descriptions
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { void loadPreview(); }}
                  isLoading={previewing}
                  loadingText="Loading preview…"
                  isDisabled={busy}
                >
                  Preview
                </Button>
            </HStack>

            <DescriptionCopyPanel
              phase={copyPhase}
              estimate={copyEstimate}
              run={copyRun}
              error={copyError}
              capInput={capInput}
              onCapInput={(value) => {
                setCapTouched(true);
                setCapInput(value);
              }}
              onGenerate={() => { void startCopy(false); }}
              onRetry={() => { void startCopy(true); }}
              onCancel={() => { void cancelCopy(); }}
              cancelling={cancelling}
            />

            <Text fontSize="xs" color="brand.muted">
              The first preview reads from Shopify and can take a moment.
            </Text>

            {previewError && <Text fontSize="sm" color="red.600">{previewError}</Text>}

            {preview && <PreviewCounts preview={preview} />}
          </VStack>
        ) : null}

        <AlertDialog
          isOpen={confirm.isOpen}
          leastDestructiveRef={cancelRef}
          onClose={confirm.onClose}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent mx={4}>
              <AlertDialogHeader fontSize="md" fontWeight="800" color="brand.ink">
                Rotate the feed URL?
              </AlertDialogHeader>
              <AlertDialogBody>
                <Text fontSize="sm" color="brand.ink">
                  The current feed URL stops working as soon as you rotate it. Meta Commerce Manager has to be updated to the new URL.
                </Text>
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} size="sm" variant="ghost" onClick={confirm.onClose} isDisabled={busy}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="purple"
                  ml={3}
                  onClick={() => { void createToken(true); }}
                  isLoading={busy}
                  loadingText="Rotating…"
                >
                  Rotate
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </CardBody>
    </Card>
  );
}

function DescriptionCopyPanel({
  phase, estimate, run, error, capInput, onCapInput, onGenerate, onRetry, onCancel, cancelling
}: {
  phase: CopyPhase;
  estimate: CopyEstimateView | null;
  run: CopyRunView | null;
  error: string | null;
  capInput: string;
  onCapInput: (value: string) => void;
  onGenerate: () => void;
  onRetry: () => void;
  onCancel: () => void;
  cancelling: boolean;
}) {
  const showEstimate = estimate != null && phase !== 'idle' && phase !== 'estimating';
  const showRun = run != null && phase !== 'idle' && phase !== 'estimating' && phase !== 'estimated';
  const cap = parseCap(capInput);
  const inFlight = phase === 'estimating' || phase === 'starting' || phase === 'polling';
  const canCancel = phase === 'polling' || phase === 'starting' || phase === 'timedOut';
  const canConfirm = showEstimate && !inFlight && phase !== 'timedOut' && cap != null;
  const retryable = phase === 'done' && run != null && ((run.rejected ?? 0) > 0 || (run.tooLong ?? 0) > 0);
  if (!showEstimate && !showRun && !error) return null;

  return (
    <Box role="region" aria-label="Catalog descriptions" borderWidth="1px" borderColor="brand.border" borderRadius="md" px={3} py={2.5}>
      {showEstimate && (
        <VStack align="stretch" spacing={2}>
          <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">Description estimate</Text>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
            <CountTile label="Products" value={estimate.products} />
            <CountTile label="Proof" value={estimate.proof} />
            <CountTile label="Skip" value={estimate.skip} />
            <CountTile label="Estimated cost" value={estimate.estimatedCostUsd} money />
          </SimpleGrid>
          {estimate.titles.length > 0 && (
            <Box>
              {estimate.titles.slice(0, 8).map((title, index) => (
                <Text key={`${title}-${index}`} fontSize="xs" color="brand.ink">{title}</Text>
              ))}
              {estimate.titles.length > 8 && (
                <Text fontSize="xs" color="brand.muted">and {estimate.titles.length - 8} more</Text>
              )}
            </Box>
          )}
          <Box>
            <Text as="label" htmlFor="catalog-copy-cap" fontSize="xs" color="brand.muted">USD cap</Text>
            <HStack mt={1} spacing={2} align="center">
              <Input
                id="catalog-copy-cap"
                aria-label="USD cap"
                size="sm"
                maxW="140px"
                inputMode="decimal"
                value={capInput}
                onChange={(event) => onCapInput(event.target.value)}
                isDisabled={inFlight}
              />
              <Button
                size="sm"
                variant="brand"
                onClick={onGenerate}
                isDisabled={!canConfirm}
                isLoading={phase === 'starting'}
                loadingText="Starting…"
              >
                Generate with cap
              </Button>
            </HStack>
            <Text fontSize="xs" color="brand.muted" mt={1}>
              Default is the estimate rounded up. Nothing runs until you confirm this cap.
            </Text>
          </Box>
        </VStack>
      )}

      {(phase === 'polling' || phase === 'starting') && (
        <HStack spacing={2} mt={showEstimate ? 3 : 0} mb={2}>
          <Spinner size="sm" />
          <Text fontSize="sm" color="brand.muted">Generating descriptions…</Text>
        </HStack>
      )}

      {showRun && (
        <Box mt={phase === 'polling' || phase === 'starting' ? 0 : showEstimate ? 3 : 0}>
          {phase === 'done' ? (
            <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" mb={2}>Descriptions ready</Text>
          ) : null}
          {phase === 'cancelled' ? (
            <Text fontSize="xs" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em" mb={2}>Descriptions cancelled</Text>
          ) : null}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2}>
            <CountTile label="Ready" value={run.ready} />
            <CountTile label="Proof" value={run.proof} />
            <CountTile label="Rejected" value={run.rejected} />
            {run.tooLong != null ? <CountTile label="Too long" value={run.tooLong} /> : null}
            {run.spentUsd != null ? <CountTile label="Spend so far" value={run.spentUsd} money /> : null}
          </SimpleGrid>
          {canCancel && (
            <Button
              size="sm"
              variant="outline"
              mt={2}
              onClick={onCancel}
              isLoading={cancelling}
              loadingText="Cancelling…"
              aria-label="Cancel description generation"
            >
              Cancel
            </Button>
          )}
          {retryable && (
            <Button size="sm" variant="outline" mt={2} onClick={onRetry} isDisabled={inFlight}>
              Retry too-long
            </Button>
          )}
        </Box>
      )}

      {error && <Text fontSize="sm" color="red.600" mt={2}>{error}</Text>}
    </Box>
  );
}

function CountTile({ label, value, money }: { label: string; value: number | null; money?: boolean }) {
  const text = value == null
    ? '—'
    : money
      ? formatUsd(value)
      : value.toLocaleString('en-US');
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="md" px={2.5} py={2}>
      <Text fontSize="10px" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">{label}</Text>
      <Text fontSize="lg" fontWeight="700" color="brand.ink" lineHeight="1.2">{text}</Text>
    </Box>
  );
}

function PreviewCounts({ preview }: { preview: FeedPreview }) {
  const counts = PREVIEW_COUNTS.filter(row => typeof preview[row.key] === 'number');
  const cacheLabel = cacheNote(preview.cache);
  return (
    <Box role="region" aria-label="Catalog preview">
      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
        {counts.map(row => (
          <Box key={row.key} borderWidth="1px" borderColor="brand.border" borderRadius="md" px={2.5} py={2}>
            <Text fontSize="10px" color="brand.muted" textTransform="uppercase" letterSpacing="0.04em">{row.label}</Text>
            <Text fontSize="lg" fontWeight="700" color="brand.ink" lineHeight="1.2">
              {(preview[row.key] as number).toLocaleString('en-US')}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
      {(preview.currency || cacheLabel) && (
        <HStack mt={2} spacing={3}>
          {preview.currency && (
            <Text fontSize="xs" color="brand.muted">Currency {preview.currency}</Text>
          )}
          {cacheLabel && <Text fontSize="xs" color="brand.muted">{cacheLabel}</Text>}
        </HStack>
      )}
    </Box>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <HStack align="flex-start" spacing={3}>
      <Text fontSize="xs" color="brand.muted" w="96px" flexShrink={0}>{label}</Text>
      <Text as="div" fontSize="sm" color="brand.ink" minW={0}>{value}</Text>
    </HStack>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function errorCode(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const code = (body as { code?: unknown }).code;
  return typeof code === 'string' && code ? code : null;
}

function loadErrorMessage(err: unknown): string {
  if (isApiError(err) && err.status === 404) return 'Brand not found.';
  if (err instanceof Error && err.message) return err.message;
  return 'Could not load the catalog feed.';
}

function cacheNote(cache: unknown): string | null {
  if (typeof cache === 'string' && cache) return cache;
  if (typeof cache === 'boolean') return cache ? 'Served from cache' : 'Live from Shopify';
  if (cache && typeof cache === 'object') {
    const record = cache as { hit?: unknown; cached?: unknown };
    if (typeof record.hit === 'boolean') return record.hit ? 'Served from cache' : 'Live from Shopify';
    if (typeof record.cached === 'boolean') return record.cached ? 'Served from cache' : 'Live from Shopify';
  }
  return null;
}

function readCopyEstimate(data: unknown): CopyEstimateView {
  const record = asRecord(data);
  const counts = asRecord(record.counts) ?? record;
  const products = readProducts(record.products ?? record.productCount);
  return {
    products: products.count,
    titles: products.titles,
    proof: finiteNumber(record.proof) ?? finiteNumber(counts.proof),
    skip: finiteNumber(record.skip) ?? finiteNumber(counts.skip) ?? finiteNumber(record.skipped) ?? finiteNumber(counts.skipped),
    estimatedCostUsd: finiteNumber(record.estimatedCostUsd) ?? finiteNumber(record.estimatedUsd) ?? finiteNumber(record.estimatedCost)
  };
}

function readProducts(value: unknown): { count: number | null; titles: string[] } {
  if (typeof value === 'number' && Number.isFinite(value)) return { count: value, titles: [] };
  if (!Array.isArray(value)) return { count: null, titles: [] };
  const titles: string[] = [];
  for (const item of value) {
    if (typeof item === 'string') {
      const title = item.trim();
      if (title) titles.push(title);
      continue;
    }
    const record = asRecord(item);
    const title = record.title ?? record.name ?? record.label;
    if (typeof title === 'string' && title.trim()) titles.push(title.trim());
  }
  return { count: value.length, titles };
}

function readCopyRun(data: unknown): CopyRunView {
  const record = asRecord(data);
  const raw = stringField(record.status) ?? stringField(record.state) ?? stringField(record.phase) ?? '';
  const normalized = raw.toLowerCase();
  const code = stringField(record.code);
  const reason = stringField(record.reason) ?? stringField(record.failureReason);
  const errorText = stringField(record.error);
  const message = stringField(record.message);
  const worker = workerOffMessage(code, reason, message, errorText);
  let phase: CopyRunView['phase'] = 'running';
  if (['done', 'complete', 'completed', 'succeeded', 'success'].includes(normalized) || record.done === true) phase = 'done';
  else if (['cancelled', 'canceled'].includes(normalized) || record.cancelled === true || record.canceled === true) phase = 'cancelled';
  else if (['failed', 'error'].includes(normalized) || worker) phase = 'failed';
  const tooLong = finiteNumber(record.tooLong) ?? finiteNumber(record.rejectedTooLong);
  const failure = phase === 'failed' || phase === 'cancelled'
    ? (worker ?? plainPieces(reason, message, errorText))
    : null;
  return {
    phase,
    ready: finiteNumber(record.ready),
    proof: finiteNumber(record.proof),
    rejected: finiteNumber(record.rejected),
    tooLong,
    spentUsd: readSpend(record),
    error: failure
  };
}

function readSpend(record: Record<string, unknown>): number | null {
  const spend = asRecord(record.spend);
  return finiteNumber(record.spentUsd)
    ?? finiteNumber(record.spendUsd)
    ?? finiteNumber(record.actualCostUsd)
    ?? finiteNumber(record.costSoFarUsd)
    ?? finiteNumber(record.spend)
    ?? finiteNumber(spend.usd)
    ?? finiteNumber(spend.spentUsd);
}

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function stringField(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Round a dollar amount up to the next cent. Exact cents stay put. */
function roundUsdUp(value: number): number {
  if (!Number.isFinite(value)) return value;
  return Math.ceil(value * 100 - 1e-8) / 100;
}

function formatCap(value: number): string {
  return value.toFixed(2);
}

function parseCap(raw: string): number | null {
  const trimmed = raw.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d*\.?\d+$/.test(trimmed)) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  return Math.round(value * 100) / 100;
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function redactSecrets(message: string): string {
  return message
    .replace(/([?&#](?:key|token|feedKey|feed_key)=)[^&\s#]+/gi, '$1••••')
    .replace(/\b(?:feed[_-]?key|token)\b\s*[:=]\s*\S+/gi, 'token ••••');
}

function looksLikeCode(value: string): boolean {
  return value.length < 80 && !/\s/.test(value);
}

function plainPieces(...values: Array<string | null>): string | null {
  for (const value of values) {
    if (!value) continue;
    const cleaned = redactSecrets(value).trim();
    if (!cleaned || looksLikeCode(cleaned)) continue;
    return cleaned;
  }
  return null;
}

function isWorkerOff(value: string): boolean {
  const slug = value.toLowerCase().replace(/[\s_]+/g, '-');
  if ([
    'worker-off',
    'worker-disabled',
    'worker-unavailable',
    'copy-worker-off',
    'copy-worker-disabled'
  ].includes(slug)) return true;
  return /copy worker is (off|disabled|unavailable)|worker is (off|disabled)/i.test(value);
}

function workerOffMessage(code: string | null, ...texts: Array<string | null>): string | null {
  const values = [code, ...texts].filter((value): value is string => Boolean(value));
  if (!values.some(isWorkerOff)) return null;
  return plainPieces(...texts)
    ?? 'The copy worker is off, so description generation cannot start.';
}

function copyErrorMessage(err: unknown, fallback: string): string {
  if (isApiError(err) && err.status === 404) return 'Brand not found.';
  if (isApiError(err)) {
    const body = asRecord(err.body);
    const code = stringField(body.code);
    const reason = stringField(body.reason) ?? stringField(body.failureReason);
    const errorText = stringField(body.error);
    const message = stringField(body.message);
    const worker = workerOffMessage(code, reason, message, errorText);
    if (worker) return worker;
    const prose = plainPieces(reason, message, errorText, err.message);
    if (prose) return prose;
  }
  const message = err instanceof Error && err.message ? err.message : '';
  return plainPieces(message) ?? fallback;
}

function FeedIcon() {
  return (
    <Icon viewBox="0 0 24 24" w="16px" h="16px" color="rsViolet.500">
      <path fill="currentColor" d="M4 5h16a1 1 0 0 1 1 1v3H3V6a1 1 0 0 1 1-1zm-1 6h18v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-7zm4 2v2h6v-2H7z" />
    </Icon>
  );
}
